import { useState } from 'react';
import { useLoaderData, useParams, useRevalidator } from 'react-router';
import { pushDomainRecordEventToDataLayer } from '../../../../../common/analytics.js';
import { CommonErrorType } from '../../../../../common/enums.js';
import { createDnsRecord, deleteDnsRecord, updateDnsRecord } from '../../../../../common/fetch.js';
import { t } from '../../../../../common/i18n';
import { DnsRecordList } from '../../../../../components/DnsManagement/DnsRecordManagement/DnsRecordList/DnsRecordList.js';
import { DnsRecordManagement } from '../../../../../components/DnsManagement/DnsRecordManagement/DnsRecordManagement.js';
import { SystemError } from '../../../../../components/SystemError/SystemError.js';
import { addNotification } from '../../../notificationStore.js';
import type { SettingsUpdateType } from '../../../../../common/analytics.js';
import type { SubscriptionId } from '../../../../../common/constants/pathInterfaces.js';
import type { DnsRecordRequest } from '../../../../../generated/api/dnsRecordRequest.js';
import type { DnsRecordsResponse } from '../../../../../generated/api/dnsRecordsResponse.js';

export const DnsRecordsPath = () => {
  const { subscriptionId } = useParams<SubscriptionId>();
  const recordsData = useLoaderData() as DnsRecordsResponse;
  const revalidator = useRevalidator();
  const [processingSubmit, setProcessingSubmit] = useState(false);

  if (!subscriptionId) {
    return <SystemError errors={[{ type: CommonErrorType.NOT_FOUND, message: 'subscriptionId missing' }]} />;
  }

  const onSubmit = async (
    updateType: SettingsUpdateType,
    submitFn: () => Promise<Response>,
    successMsg: string,
    failureMsg: string
  ) => {
    setProcessingSubmit(true);
    try {
      await submitFn();
      addNotification(successMsg, 'success');
      pushDomainRecordEventToDataLayer(updateType);
    } catch {
      addNotification(failureMsg, 'error');
    } finally {
      setProcessingSubmit(false);
      revalidator.revalidate();
    }
  };

  const onCreate = async (payload: DnsRecordRequest) =>
    onSubmit(
      'add',
      () => createDnsRecord(subscriptionId, payload),
      t.KG30('New record was created'),
      t.PODN('DNS record add failed!')
    );

  const onUpdate = async (recordId: number, payload: DnsRecordRequest) =>
    onSubmit(
      'modify',
      () => updateDnsRecord(subscriptionId, recordId, payload),
      t.VFZ2('Changes successfully saved'),
      t.HDQI('DNS record update failed!')
    );

  const onDelete = async (recordId: number) =>
    onSubmit(
      'remove',
      () => deleteDnsRecord(subscriptionId, recordId),
      t.B9GC('Record deleted'),
      t.XUDO('DNS record delete failed!')
    );

  return (
    <DnsRecordManagement subscriptionId={subscriptionId} domainName={recordsData.domainName} spinner={processingSubmit}>
      <DnsRecordList recordsData={recordsData} onCreate={onCreate} onUpdate={onUpdate} onDelete={onDelete} />
    </DnsRecordManagement>
  );
};
